import { useState } from 'react';
import { Info, Mail, MapPin, Smartphone, Linkedin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import packageJson from '../../package.json';
import { getActivePortal, getAuthHeaders } from '../utils/auth';

export default function AboutPage() {
  const [category, setCategory] = useState('General');
  const [message, setMessage] = useState('');
  const [messageHtml, setMessageHtml] = useState('');
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const portal = getActivePortal() || 'student';
    const headers = getAuthHeaders(portal);

    const fd = new FormData();
    fd.append('category', category);
    fd.append('messageHtml', messageHtml || message);
    // attach files
    files.forEach((f) => {
      fd.append('files', f, f.name);
    });

    fetch('/api/core/feedback/', {
      method: 'POST',
      headers: {
        ...headers,
        // don't set Content-Type; browser will set multipart boundary
      },
      body: fd,
    })
      .then(async (res) => {
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || 'Failed to submit feedback');
        }
        return res.json();
      })
      .then(() => {
        setSubmitted(false);
        setMessage('');
        setMessageHtml('');
        setCategory('General');
        setFiles([]);
        setShowConfirm(true);
      })
      .catch((err) => {
        console.error('Feedback submit error', err);
        setSubmitted(false);
        alert('Could not submit feedback. Please try again later.');
      });
  };

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto max-h-screen overflow-auto">
      <div className="mb-5 sm:mb-6 text-center lg:text-left">
        <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4">
          <Info className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--green-bright))]" />
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">About Budlee AI</h1>
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">Learn about Budlee AI, our mission, partners and contact details</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="p-3 sm:p-4 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg flex flex-col">
          <h3 className="font-semibold mb-2 text-foreground text-base sm:text-lg">Send Feedback</h3>
          <form onSubmit={handleSubmit} className="space-y-3 flex flex-col h-full">
            <label className="block text-xs sm:text-sm text-[hsl(var(--muted-foreground))]">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full bg-transparent border-[hsl(var(--card-border))] text-foreground text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <SelectItem value="General" className="text-white hover:bg-[hsl(var(--sidebar-hover))] focus:bg-[hsl(var(--sidebar-hover))] cursor-pointer">
                  General
                </SelectItem>
                <SelectItem value="Bug" className="text-white hover:bg-[hsl(var(--sidebar-hover))] focus:bg-[hsl(var(--sidebar-hover))] cursor-pointer">
                  Bug
                </SelectItem>
                <SelectItem value="Feature Request" className="text-white hover:bg-[hsl(var(--sidebar-hover))] focus:bg-[hsl(var(--sidebar-hover))] cursor-pointer">
                  Feature Request
                </SelectItem>
                <SelectItem value="Other" className="text-white hover:bg-[hsl(var(--sidebar-hover))] focus:bg-[hsl(var(--sidebar-hover))] cursor-pointer">
                  Other
                </SelectItem>
              </SelectContent>
            </Select>

            <label className="block text-xs sm:text-sm text-[hsl(var(--muted-foreground))]">Message</label>
            <div className="border rounded-md bg-transparent border-[hsl(var(--card-border))] p-2 flex flex-col flex-1 min-h-0">
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => document.execCommand('bold')} className="px-2 py-1 bg-[hsl(var(--card-bg))] rounded text-foreground hover:bg-[hsl(var(--sidebar-hover))]/30">B</button>
                <button type="button" onClick={() => document.execCommand('italic')} className="px-2 py-1 bg-[hsl(var(--card-bg))] rounded text-foreground hover:bg-[hsl(var(--sidebar-hover))]/30">I</button>
                <button type="button" onClick={() => document.execCommand('underline')} className="px-2 py-1 bg-[hsl(var(--card-bg))] rounded text-foreground hover:bg-[hsl(var(--sidebar-hover))]/30">U</button>
              </div>
              <div
                contentEditable
                role="textbox"
                aria-multiline
                suppressContentEditableWarning
                onInput={(e) => setMessageHtml(e.currentTarget.innerHTML)}
                className="flex-1 min-h-0 overflow-auto text-[hsl(var(--muted-foreground))] focus:outline-none"
                placeholder="Describe your feedback..."
              />
            </div>

            <div className="mt-2">
              <label className="block text-xs sm:text-sm text-[hsl(var(--muted-foreground))] mb-1">Attach file (optional)</label>
              <input
                type="file"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="w-full text-xs sm:text-sm text-[hsl(var(--muted-foreground))]"
              />
              {files.length > 0 && (
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Selected: {files.map(f => f.name).join(', ')}</p>
              )}
            </div>

            <div className="flex items-center justify-end">
              <button
                type="submit"
                disabled={submitted}
                className="px-4 py-2 bg-[hsl(var(--teal-vivid))] text-white rounded-md hover:opacity-95 disabled:opacity-60 text-sm"
              >
                {submitted ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </div>

        <div className="p-3 sm:p-4 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg flex flex-col">
          <h3 className="font-semibold mb-2 text-foreground text-base sm:text-lg">Company</h3>
          <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))]">A product of Futurvy Pvt. Ltd.</p>

          <h3 className="font-semibold mt-4 mb-2 text-foreground text-base sm:text-lg">Address</h3>
          <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))]">123 Innovation Drive, Tech Park, Kannur, KL 676528</p>

          <h3 className="font-semibold mt-4 mb-2 text-foreground text-base sm:text-lg">Contact</h3>
          <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2"><Mail className="w-4 h-4" /> help@budlee.ai</p>
          <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2 mt-1"><Smartphone className="w-4 h-4" /> +91 9995 705 429</p>

          <div className="mt-8 grid grid-cols-2 gap-y-8 gap-x-2 sm:gap-x-6 items-end justify-items-center">
            <img src="/assets/partners/startup_india.png" alt="Startup India" className="h-10 md:h-12 object-contain" onContextMenu={(e) => e.preventDefault()} />
            <img src="/assets/partners/startup_kerala.png" alt="Kerala Startup Mission" className="h-20 md:h-24 object-contain" onContextMenu={(e) => e.preventDefault()} />

            <div
              className="h-10 md:h-12 w-32 md:w-40 bg-[#DA592D] self-center"
              style={{
                maskImage: 'url(/assets/partners/startup_microsoft.png)',
                WebkitMaskImage: 'url(/assets/partners/startup_microsoft.png)',
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center'
              }}
              onContextMenu={(e) => e.preventDefault()}
              title="Microsoft for Startups"
            />
            <img
              src="/assets/partners/startup_nvidia.png"
              alt="NVIDIA Inception"
              className="h-10 md:h-12 object-contain self-center"
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          <div className="mt-4 flex flex-col">
            <div className="mt-auto flex items-end justify-center gap-4 sm:gap-6 flex-wrap w-full min-h-[48px]">
              <img src="/assets/partners/startup_india.png" alt="Startup India" className="h-10 md:h-12 object-contain" />
              <img src="/assets/partners/startup_kerala.png" alt="Kerala Startup Mission" className="h-20 md:h-24 object-contain" />
              <img src="/assets/partners/startup_microsoft.png" alt="Microsoft for Startups" className="h-10 md:h-12 object-contain" />
            </div>
            <div className="w-full flex justify-center mt-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold mb-0 text-foreground text-sm sm:text-base">App Version :</h3>
                <span className="text-xs sm:text-sm text-foreground font-semibold">{packageJson.version || 'v1.0.0'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-[hsl(var(--card-bg))] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Thanks for your feedback</DialogTitle>
            <DialogDescription className="text-white">We appreciate you taking time to share your thoughts. Our team will review it and get back if necessary.</DialogDescription>
          </DialogHeader>
          <div className="pt-2" />
          <DialogFooter>
            <Button variant="default" onClick={() => setShowConfirm(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
